import { Component, OnInit, computed, effect, inject, input, model } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { DisplayType } from '@models/module/fields/enums';
import {
  AddressModel,
  DefaultAddress,
  getAddressModelFromAzureMapAddressAndPosition,
} from '@models/module/fields/main';
import { AzureMapAddressSearchResult } from '@shared/components/azure-map/azure-map.model';
import { AzureMapService } from '@shared/components/azure-map/azure-map.service';
import { DEBOUNCE_TIMES } from '@shared/consts';
import { CipoFormModule } from '@shared/modules/forms.module';
import { Observable, debounceTime, filter, switchMap } from 'rxjs';
import { CipoAddressService } from '../cipo-address.service';

@Component({
  selector: 'cipo-address-form',
  templateUrl: './cipo-address-form.component.html',
  imports: [CipoFormModule],
})
export class CipoAddressFormComponent implements OnInit {
  private azureMapService = inject(AzureMapService);
  private fb = inject(FormBuilder);
  addressService = inject(CipoAddressService);

  address = model<AddressModel>(undefined);
  editMode = input<boolean>(false);
  multiple = input<boolean>(false);
  index = input<number>(undefined);
  displayType = input<DisplayType>(DisplayType.Address);
  displayTypeReadOnly = computed<DisplayType>(() =>
    this.displayType() === DisplayType.AddressLocationName ? DisplayType.Address : this.displayType(),
  );

  addressChangedEffect = effect(() => {
    const address = this.address() ?? { ...DefaultAddress };
    this.addressForm.patchValue(address, { emitEvent: false });

    if (this.editMode()) {
      // this will trigger validation and show errors if the form is invalid when the address changes from outside of the form
      this.addressForm.markAllAsTouched();
    }
  });

  addressResults$: Observable<AzureMapAddressSearchResult[]>;
  addressForm = this.fb.group<AddressModel>({ ...DefaultAddress });

  constructor() {
    // handle form value changes — addressChanged() returns early when editMode is false
    this.addressForm.valueChanges.pipe(debounceTime(DEBOUNCE_TIMES.default)).subscribe(_ => this.addressChanged());

    // handle address suggestions
    this.addressResults$ = this.addressForm.controls.address.valueChanges.pipe(
      debounceTime(DEBOUNCE_TIMES.default),
      filter(address => address !== undefined && address !== ''),
      switchMap(address => this.azureMapService.getAddressSuggestions(address)),
    );
  }

  ngOnInit(): void {
    if (!this.editMode()) {
      return;
    }

    // initialize form validators
    this.addressForm.controls.address.addValidators(Validators.required);
    this.addressForm.controls.locationName.addValidators(Validators.required);
    this.addressForm.controls.lat.addValidators(Validators.required);
    this.addressForm.controls.lon.addValidators(Validators.required);
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent) {
    // set address form values based on selected suggestion
    const { address, position } = event.option.value as AzureMapAddressSearchResult;
    this.addressForm.patchValue(getAddressModelFromAzureMapAddressAndPosition(address, position));
  }

  getAddressText(option: AzureMapAddressSearchResult | string) {
    if (typeof option === 'string') {
      return option;
    }
    return option?.address?.freeformAddress ? option.address.freeformAddress : '';
  }

  private addressChanged() {
    if (!this.editMode()) {
      return;
    }
    const value = this.addressForm.value as AddressModel;
    this.address.set({ ...value, lat: +value.lat, lon: +value.lon });
  }
}
